/** @module The user's storage locations: one cached query, ordered as a tree for lists. */
import { useQuery } from "@tanstack/react-query";

import { api, type components, problemFrom } from "@/api/client";

export type Location = components["schemas"]["LocationOut"];

export async function fetchLocations() {
  const { data, response } = await api.GET("/api/v1/locations", {});
  if (!data) throw problemFrom(response);
  return data;
}

export function useLocations(enabled = true) {
  return useQuery({ queryKey: ["locations"], queryFn: fetchLocations, enabled });
}

/** Each place after its parent, with its depth for indenting; siblings by name. */
export function treeOrder(locations: Location[]): { location: Location; depth: number }[] {
  const known = new Set(locations.map((location) => location.id));
  const children = new Map<string | null, Location[]>();
  for (const location of locations) {
    // A parent that is gone (or someone else's) makes the place a top-level one.
    const parent = location.parent_id && known.has(location.parent_id) ? location.parent_id : null;
    children.set(parent, [...(children.get(parent) ?? []), location]);
  }
  const ordered: { location: Location; depth: number }[] = [];
  const seen = new Set<string>();
  const visit = (parent: string | null, depth: number) => {
    const level = [...(children.get(parent) ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
    for (const location of level) {
      if (seen.has(location.id)) continue;
      seen.add(location.id);
      ordered.push({ location, depth });
      visit(location.id, depth + 1);
    }
  };
  visit(null, 0);
  return ordered;
}

/** The ids a place may move under: everything except itself and what lies inside it. */
export function possibleParents(locations: Location[], id: string): Location[] {
  const inside = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const location of locations) {
      if (location.parent_id && inside.has(location.parent_id) && !inside.has(location.id)) {
        inside.add(location.id);
        grew = true;
      }
    }
  }
  return locations.filter((location) => !inside.has(location.id));
}
